import { Component, ViewChild, Input } from '@angular/core';
import { PopoverTriggerDirective } from '../popover/popover-trigger.directive';
import { PopoverDirective } from '../popover/popover.directive';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { SbEventBusService } from '../../../legacy/services';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { AppConfigQuery } from '../../state/app-config/app-config.query';
import { SettingsService } from '../../../features/settings/state/settings.service';
import { SessionQuery } from '../../state/session/session.query';
import { CurrencyCountryCodes } from 'src/app/shared/components/currency-selector/currency-country-codes';
import { UserSettingsQuery } from '../../../features/user-settings/state/user-settings.query';
import { UserSettingsService } from '../../../features/user-settings/state/user-settings.service';
import { ImpersonateQuery } from '../../../pages/admin/impersonate/state/impersonate.query';
import { ImpersonateService } from '../../../pages/admin/impersonate/state/impersonate.service';
import { LegalEntityQuery } from '../../../pages/admin/state/legal-entities/legal-entity.query';
import { LegalEntity } from '../../../pages/admin/state/legal-entities/legal-entity.model';
import { LegalEntityService } from '../../../pages/admin/state/legal-entities/legal-entity.service';
import { combineQueries } from '@datorama/akita';
import { fadeInOut } from '../../../shared/animations/fade-in-out.animation';
import { combineLatest } from 'rxjs';

@Component({
    selector: 'app-sb-profile',
    templateUrl: './sb-profile.component.html',
    styleUrls: ['./sb-profile.component.scss'],
    animations: [fadeInOut]
})
export class SbProfileComponent {
    _showUsername: boolean;
    get showUsername() {
        return this._showUsername;
    }

    @Input() set showUsername(value) {
        this._showUsername = value;
    }

    user$ = combineLatest([
        this._sessionQuery.userName$,
        this._sessionQuery.firstName$
    ]).pipe(map(([userName, firstName]) => firstName || userName));
    userEmail$ = this._sessionQuery.userEmail$;
    baseCurrency$ = this._userSettingsQuery
        .selectGlobalSetting$('BASE_CURRENCY')
        .pipe(shareReplay(1));
    codes = CurrencyCountryCodes;
    options$ = this.baseCurrency$.pipe(
        switchMap((baseCurrency) =>
            this._settingsService.getCurrenciesAndRates(baseCurrency)
        )
    );

    @ViewChild(PopoverTriggerDirective) trigger: PopoverTriggerDirective;
    @ViewChild(PopoverDirective) popover: PopoverDirective;
    constructor(
        public dialog: MatDialog,
        private _http: HttpClient,
        private _appConfig: AppConfigQuery,
        private _eventBus: SbEventBusService,
        private _router: Router,
        private _settingsService: SettingsService,
        private _sessionQuery: SessionQuery,
        private _userSettingsQuery: UserSettingsQuery,
        private _userSettingsService: UserSettingsService,
        private _impersonateQuery: ImpersonateQuery,
        private _impersonateService: ImpersonateService,
        private _legalEntityQuery: LegalEntityQuery,
        private _legalEntityService: LegalEntityService
    ) {}

    impersonatedUser$ = this._impersonateQuery.selectImpersonatedUser$;

    legalEntities$ = combineQueries([
        this._legalEntityQuery.all$,
        this._legalEntityQuery.active$
    ]).pipe(
        map(([legalEntities]) =>
            legalEntities.reduce((acc, legalEntity) => {
                if (
                    legalEntity &&
                    legalEntity.metadata?.includeInGlobalFilter !== false
                ) {
                    acc.push({
                        ...legalEntity,
                        selected: this._legalEntityQuery.isSelected(legalEntity.ownerId)
                    });
                }
                return acc;
            }, [])
        ),
        shareReplay(1)
    );
    legalEntitySelection$ = this.legalEntities$.pipe(
        map((legalEntities) => {
            const selected = legalEntities.filter((entity) => entity.selected);
            //all legal entities selected';
            if (selected.length === legalEntities.length) {
                return {
                    length: legalEntities.length,
                    text: 'all',
                    show: false
                };
            }
            switch (selected.length) {
                case 0:
                    return {
                        length: selected.length,
                        text: 'Please select a legal entity!',
                        show: true
                    };
                case 1:
                    return {
                        length: selected.length,
                        text: `selected ${
                            selected[0].displayName ||
                            selected[0].shortName ||
                            selected[0].longName
                        }`,
                        show: true
                    };
                default:
                    return {
                        length: selected.length,
                        text: `selected ${selected.length} out of ${legalEntities.length} legal entities`,
                        show: true
                    };
            }
        }),
        shareReplay(1)
    );

    setBaseCurrency(currency: string) {
        // persist the updated base currency using the user settings service
        this._userSettingsService
            .saveGlobalSetting('BASE_CURRENCY', currency)
            .subscribe();
    }
    logout() {
        this._router.navigate([{ outlets: { modal: 'logout' } }]);
    }

    showSettings() {
        this._router.navigate([{ outlets: { modal: 'settings' } }]);
    }
    showImpersonate() {
        this._router.navigate([{ outlets: { modal: ['admin', 'impersonate'] } }]);
    }
    endImpersonation() {
        this._impersonateService.endImpersonation();
    }

    toggleLegalEntity(legalEntity: LegalEntity) {
        this._legalEntityService.toggleSelectedState(legalEntity);
    }

    toggleAllLegalEntity(state: boolean) {
        this._legalEntityService.setSelectedState(this._legalEntityQuery.all, !state);
    }
}
